import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import { Box, DialogActions, DialogContent, Toolbar, useTheme } from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { TextField } from "../../../TextField";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { useAddExchangeRate } from "../../../../hooks/exchangeRates/useAddExchangeRate";
import { useModifyExchangeRate } from "../../../../hooks/exchangeRates/useModifyExchangeRate";
import { useDeleteExchangeRate } from "../../../../hooks/exchangeRates/useDeleteExchangeRate";
import { IExchangeRate } from "../../../../hooks/exchangeRates";
import { DatePickerField } from "../../../DatePickerField";
import { AlertDialog } from "../../../AlertDialog";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { StringFormat } from "../../../../models/StringFormat";
import { FieldTitle } from "../../../Text/FieldTitle";
import { ContainedButton } from "../../../ContainedButton";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { useUserProfile } from "../../../../hooks/session";
import { Firebase } from "../../../../services/Firebase";
import { ProductPurchasesDialog } from "../../../products/ProductPurchasesDialog";
import { ProductType } from "../../../../hooks/products/interfaces";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";

export const ModifyExchangeRateDialog: FunctionComponent<IModifyExchangeRateDialogProps> = ({
  open,
  onClose,
  exchangeRate,
  onNewItem,
  onModifiedItem,
  onDeletedItem
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [currentUserProfile] = useUserProfile.useState();
  const dialogTitle = exchangeRate ? strings.exchange_rate_title : strings.mer_new_title;
  const { addExchangeRate } = useAddExchangeRate();
  const { modifyExchangeRate } = useModifyExchangeRate();
  const { deleteExchangeRate } = useDeleteExchangeRate();
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [isPurchaseAlertOpen, setIsPurchaseAlertOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const validationSchema = Yup.object({
    rate: Yup.number()
      .typeError(strings.mer_format_error)
      .required(strings.field_missing)
      .positive(strings.mer_format_error),
    date: Yup.date().required(strings.field_missing)
  });
  const formik = useFormik({
    initialValues: {
      rate: exchangeRate?.rate ?? 1,
      date: exchangeRate ? new Date(exchangeRate.unixTimestamp * 1000) : undefined
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ rate, date }) => {
      if (rate == undefined || date == undefined) {
        return;
      }
      setIsLoading(true);
      if (exchangeRate) {
        const { status, body } = await modifyExchangeRate({
          uuid: exchangeRate.uuid,
          unixTimestamp: date.getTime() / 1000,
          rate: rate
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_exchangerate_action", { isNew: false });
          onModifiedItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      } else {
        const { status, body } = await addExchangeRate({
          unixTimestamp: date.getTime() / 1000,
          rate: rate
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_exchangerate_action", { isNew: true });
          onNewItem?.(body);
          onClose();
        } else if (StatusCodesHelper.isLimitReached(status)) {
          setAlertMessage(body.message);
          setIsPurchaseAlertOpen(true);
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  const onDelete = async () => {
    if (!exchangeRate) return;
    setIsLoading(true);
    const { status, body } = await deleteExchangeRate({ uuid: exchangeRate.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      onDeletedItem?.(exchangeRate);
      onClose();
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {exchangeRate && (
            <ToolbarButton
              disabled={isLoading}
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0 }}>
          <FieldTitle>
            {StringFormat(strings.mer_rate_title, currentUserProfile?.currency.code ?? "")}
          </FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="rate"
              name="rate"
              size="small"
              autoFocus={!isMobile}
              placeholder={StringFormat(
                strings.mer_rate_placeholder,
                currentUserProfile?.currency.code ?? ""
              )}
              value={formik.values.rate}
              onChange={formik.handleChange}
              enterAction={formik.submitForm}
            />
          </div>
          <FieldTitle>{strings.mer_date_title}</FieldTitle>
          <DatePickerField
            className={styles.datePicker}
            title={strings.mer_date_title}
            value={formik.values.date ?? null}
            onChange={newValue => formik.setFieldValue("date", newValue)}
            disabled={exchangeRate !== undefined}
          />
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} disabled={isLoading} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
      <AlertDialog
        message={alertMessage}
        open={isPurchaseAlertOpen}
        setOpen={setIsPurchaseAlertOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setIsPurchaseDialogOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.EXCHANGE_RATES}
        open={isPurchaseDialogOpen}
        setOpen={setIsPurchaseDialogOpen}
      />
    </FormikProvider>
  );
};

export interface IModifyExchangeRateDialogProps {
  open: boolean;
  onClose: () => void;
  exchangeRate?: IExchangeRate;
  onNewItem?: (item: IExchangeRate) => void;
  onModifiedItem?: (item: IExchangeRate) => void;
  onDeletedItem?: (item: IExchangeRate) => void;
}
